/**
 * Chinese Legal Translations
 * 法律文档翻译模块 - 中文
 * 
 * 包含隐私政策 (privacy) 和服务条款 (terms) 等法律文档的翻译
 */

import type { LegalTranslations } from '../../types';

export const zhLegal: LegalTranslations = {
  privacy: {
    title: '隐私政策',
    subtitle: '我们如何收集、使用和保护您的个人信息。',
    lastUpdated: '最后更新',
    effectiveDate: '生效日期',
    meta: {
      lastUpdatedValue: '2026年2月6日',
      effectiveDateValue: '2026年2月6日',
    },
    introduction: '福建晶辉环境科技有限公司（"我们"、"我们的"或"本公司"）承诺保护您的隐私。本隐私政策说明了我们如何在您访问我们的网站和使用我们的服务时收集、使用、披露和保护您的信息。',
    sections: {
      infoCollect: {
        title: '1. 我们收集的信息',
        description: '当您自愿向我们提供以下信息时，我们可能会收集您的个人信息：',
        items: [
          '在我们的网站上注册或进行咨询',
          '请求报价或产品信息',
          '订阅我们的新闻通讯',
          '联系我们的销售或支持团队',
          '参与调查或促销活动',
        ],
      },
      typesCollected: {
        title: '2. 收集的信息类型',
        items: [
          '个人识别信息：姓名、电子邮箱、电话号码、公司名称、职位',
          '人口统计信息：国家、地区、语言偏好',
          '技术信息：IP地址、浏览器类型、操作系统、引荐网址',
          '通讯数据：消息、咨询、反馈和支持请求',
        ],
      },
      howUse: {
        title: '3. 我们如何使用您的信息',
        description: '我们收集的信息用于：',
        items: [
          '提供、维护和改进我们的产品和服务',
          '回复您的咨询并提供客户支持',
          '向您发送产品信息、报价和促销材料',
          '处理交易并发送相关信息',
          '监控和分析趋势、使用情况和活动',
          '检测、调查和防止欺诈交易',
          '遵守法律义务',
        ],
      },
      dataSecurity: {
        title: '4. 数据安全',
        description: '我们实施适当的技术和组织安全措施，以保护您的个人信息免受未经授权的访问、更改、披露或破坏。但是，通过互联网传输或电子存储没有100%安全的方法。',
        encryption: '我们使用行业标准的加密（SSL/TLS）来保护浏览器与我们的服务器之间传输的数据。',
      },
      cookies: {
        title: '5. Cookie和跟踪技术',
        description: '我们的网站可能使用Cookie、网络信标和类似的跟踪技术来收集您浏览活动的信息。您可以设置浏览器拒绝所有Cookie，或在发送Cookie时进行指示。',
      },
      thirdParty: {
        title: '6. 第三方披露',
        description: '我们不会出售、交易或以其他方式向外部方转让您的个人识别信息，以下情况除外：',
        items: [
          '与协助我们业务运营的服务提供商',
          '当法律、传票或其他法律程序要求时',
          '为了保护我们的权利、隐私、安全或财产',
          '与合并、收购或资产出售相关',
        ],
      },
      dataRetention: {
        title: '7. 数据保留',
        description: '我们将保留您的个人信息，只要有必要履行本隐私政策中概述的目的，除非法律要求或允许更长的保留期限。',
      },
      yourRights: {
        title: '8. 您的权利',
        description: '您有权：',
        items: [
          '访问并接收您的个人数据副本',
          '更正不准确的个人数据',
          '请求删除您的个人数据',
          '反对处理您的个人数据',
          '请求限制处理',
          '数据可携性',
        ],
      },
      children: {
        title: '9. 儿童隐私',
        description: '我们的网站不面向16岁以下的儿童。我们不会故意收集16岁以下儿童的个人信息。',
      },
      international: {
        title: '10. 国际数据传输',
        description: '您的信息可能会被传输到您居住国以外的国家并进行处理。这些国家的数据保护法律可能与您所在国家的法律不同。',
      },
      changes: {
        title: '11. 本政策的变更',
        description: '我们可能会不时更新本隐私政策。我们将通过在本页面上发布新的隐私政策并更新本政策顶部的"最后更新"日期来通知您任何更改。',
      },
      contactUs: {
        title: '12. 联系我们',
        description: '如果您对本隐私政策或我们的数据实践有任何疑问，请联系我们：',
        email: '电子邮箱',
        address: '地址',
        company: '福建晶辉环境科技有限公司',
        addressLine: '福建省福州市马尾区亭江镇亭旺路26号万洋科技城B区B25座',
      },
    },
  },
  terms: {
    title: '服务条款',
    subtitle: '请在使用我们的网站和服务之前仔细阅读这些条款。',
    lastUpdated: '最后更新',
    effectiveDate: '生效日期',
    meta: {
      lastUpdatedValue: '2026年2月6日',
      effectiveDateValue: '2026年2月6日',
    },
    introduction: '欢迎访问福建晶辉环境科技有限公司的网站。通过访问或使用本网站，您同意受这些服务条款以及所有适用法律法规的约束。如果您不同意这些条款的任何部分，则不得使用我们的网站。',
    importantNotice: {
      title: '重要通知',
      description: '使用本网站即表示您已阅读、理解并同意受这些服务条款的约束。如果您代表公司或组织使用本网站，您表示您有权使该实体受这些条款的约束。',
    },
    sections: {
      useWebsite: {
        title: '1. 网站使用',
        description: '福建晶辉环境科技有限公司仅出于提供信息的目的提供本网站。使用本网站即表示您同意：',
        items: [
          '仅将网站用于合法目的并符合这些条款',
          '不以任何可能损坏、禁用、负担或损害网站的方式使用网站',
          '不要试图未经授权访问网站的任何部分',
          '不要使用网站传输任何病毒、蠕虫或有害代码',
          '尊重JH Heatsup和他人的知识产权',
        ],
      },
      intellectualProperty: {
        title: '2. 知识产权',
        description: '本网站上所有内容，包括但不限于文本、图形、徽标、图像、音频剪辑、下载和软件，均为福建晶辉环境科技有限公司或其内容供应商的财产，并受国际版权、商标和其他知识产权法的保护。',
        restriction: '未经我们事先书面同意，您不得复制、分发、修改、创建衍生作品、公开展示、公开表演或利用本网站的任何内容。',
      },
      productInfo: {
        title: '3. 产品信息和定价',
        description: '本网站上呈现的所有产品和服务均视供应情况而定。我们保留随时停止任何产品或服务的权利。',
        pricing: '价格和规格如有更改，恕不另行通知。我们尽一切努力确保产品信息的准确性，但可能会出现错误。我们保留更正任何错误、不准确或遗漏并随时更改或更新信息的权利。',
        currency: '除非另有说明，所有价格均以美元报价。运费、税费和关税将额外计算，并在订购过程中计算。',
      },
      quotesOrders: {
        title: '4. 报价和订单',
        description: '通过本网站提供的报价有效期为30天，除非另有说明。所有订单须经JH Heatsup接受。',
        contract: '提交订单即表示您同意按照我们当时的条款（包括定价、运输和付款条款）购买产品。只有在我们确认接受您的订单时，才形成具有约束力的合同。',
      },
      warranties: {
        title: '5. 保修和免责声明',
        description: '本网站以及通过其提供的所有内容、产品和服务均按"原样"和"可用"提供，不提供任何类型的明示或暗示保修。',
        disclaimer: 'JH Heatsup免责声明所有明示或暗示的保修，包括但不限于暗示的适销性、特定目的适用性、标题和非侵权保证。我们不保证网站将不间断、及时、安全或无错误。',
      },
      liability: {
        title: '6. 责任限制',
        description: '在任何情况下，福建晶辉环境科技有限公司、其高级职员、董事、员工或代理人均不对因您使用或与您使用本网站或从我们购买的任何产品或服务相关的任何间接、附带、特殊、后果性或惩罚性损害承担责任。',
        limit: '对于因这些条款或我们的产品和服务引起的或与之相关的任何索赔，我们的总赔偿责任不得超过您为引起此类索赔的产品或服务支付的金额。',
      },
      privacy: {
        title: '7. 隐私政策',
        description: '您使用本网站也受我们的隐私政策的约束，该政策通过引用并入这些条款。请查阅我们的隐私政策，了解我们如何收集、使用和保护您的信息。',
      },
      thirdPartyLinks: {
        title: '8. 第三方链接',
        description: '本合同可能包含指向非JH Heatsup所有或控制的第三方网站的链接。我们对这些第三方网站的内容、隐私政策或做法没有控制权，也不承担任何责任。您自行承担访问此类链接的风险。',
        recommendation: '我们鼓励您查看您访问的任何第三方网站的条款和隐私政策。',
      },
      governingLaw: {
        title: '9. 管辖法律和争议',
        description: '这些条款应受中华人民共和国法律管辖并根据其解释，不考虑其法律冲突规定。',
        disputes: '因这些条款或您使用本合同而引起的任何争议，应通过双方之间的善意谈判解决。如果此类谈判失败，争议应根据中国国际经济贸易仲裁委员会的规则提交仲裁解决。',
      },
      export: {
        title: '10. 出口管制',
        description: '您同意遵守所有适用的出口管制法律法规。您表示您不在受美国全面制裁或禁运的国家控制之下，也不是其国民或居民。',
      },
      termination: {
        title: '11. 终止',
        description: '我们可以立即终止或暂停您访问本网站的权限，恕不另行通知，也无需承担任何责任，包括违反这些条款。终止后，您使用网站的权利将立即停止。',
      },
      changes: {
        title: '12. 这些条款的变更',
        description: '我们保留随时修改或更换这些条款的权利。如果修改是重大的，我们将在任何新条款生效前30天提供通知。您在作出此类更改后继续使用网站即表示接受新条款。',
      },
      severability: {
        title: '13. 可分割性',
        description: '如果这些条款的任何规定被认定为无效、非法或不可执行，该规定应被修改至使其可执行的最低必要程度，其余规定将继续完全有效。',
      },
      contactUs: {
        title: '14. 联系方式',
        description: '如果您对这些服务条款有任何疑问，请联系我们：',
        email: '电子邮箱',
        address: '地址',
        company: '福建晶辉环境科技有限公司',
        addressLine: '福建省福州市马尾区亭江镇亭旺路26号万洋科技城B区B25座',
      },
    },
  },
  installationGuide: {
    header: {
      title: '安装指南',
      subtitle: '按照以下步骤安全高效地安装您的JH Heatsup取暖器。',
    },
    safety: {
      title: '安全第一',
      description:
        '安装必须由合格的电工按照当地电气规范进行。在开始安装之前，请务必在主配电板处切断电源。',
    },
    toolsTitle: '所需工具',
    tools: {
      electricDrill: '电钻',
      screwdriver: '螺丝刀',
      level: '水平仪',
      measuringTape: '卷尺',
      wireStrippers: '剥线钳',
      ladder: '梯子',
    },
    stepsTitle: '安装步骤',
    steps: {
      step1: {
        title: '定位与测量',
        description:
          '选择天花板或高墙上的位置。为获得最佳性能，取暖器应安装在距地面2.2米至2.5米处。根据包装盒中提供的安装支架尺寸标记钻孔位置。',
        bullet1: '与侧墙保持最小0.5米的距离。',
        bullet2: '与天花板保持最小0.2米的距离（壁挂安装时）。',
      },
      step2: {
        title: '安装支架',
        description:
          '在标记位置钻孔并插入膨胀螺栓。将安装支架牢固地固定在表面上。将取暖器的后部滑动支架滑入已安装的支架中，并拧紧调节螺丝以设置所需角度（0°、15°、30°或45°）。',
      },
      step3: {
        title: '电气连接',
        description:
          '将电源线连接到市电。确保正确接地。对于带遥控器的型号，请确保接收器指示灯可见，以便遥控器正常工作。',
        wireLive: 'L — 火线',
        wireLiveColor: '棕色',
        wireNeutral: 'N — 零线',
        wireNeutralColor: '蓝色',
        wireEarth: 'E — 地线',
        wireEarthColor: '黄绿双色',
      },
      step4: {
        title: '测试',
        description:
          '接通电源。取暖器应在5分钟内达到满负荷运行温度。首次使用时可能会有轻微气味，这是正常现象，会很快消散。',
      },
    },
    download: {
      title: '查看产品手册',
      subtitle: '在线浏览包含您特定型号图解的完整安装手册。',
      jhnr: '查看手册',
      industrial: '查看手册',
    },
  },
  warrantyPolicy: {
    header: {
      title: '保修政策',
      subtitle: '我们对品质的承诺，让您安心无忧。',
    },
    overview: {
      standardCoverage: '标准保修',
      standardCoverageDesc:
        'JH Heatsup产品在正常使用情况下保证不存在材料和工艺缺陷。',
      warrantyPeriod: '保修期限',
      residentialPeriod: '家用取暖器保修2年。',
      industrialPeriod: '工业取暖器及控制器保修1年。',
    },
    sections: {
      scope: {
        title: '1. 保修范围',
        description:
          '本保修适用于产品的原始购买者，不可转让。保修期内，产品在材料或工艺上出现的任何缺陷部件均可维修或更换。',
      },
      notCovered: {
        title: '2. 以下情况不在保修范围内',
        description: '本保修不涵盖以下原因造成的损坏：',
        item1: '未按照提供的说明书进行不当安装。',
        item2: '误用、滥用、疏忽或意外事故。',
        item3: '产品的更改或改装。',
        item4: '电涌或雷击。',
        item5: '正常磨损（如随时间推移的表面变色）。',
      },
      claim: {
        title: '3. 如何申请保修',
        description: '如需保修服务，请联系我们的客服团队并提供以下信息：',
        proofOfPurchase: '购买凭证（发票/收据）',
        defectDescription: '缺陷的描述及照片/视频',
        serialNumber: '设备序列号',
        contactEmail: '联系邮箱',
      },
    },
  },
};
